/**
 * Quill AI Styles — tab table, key-field map, and card pill colours.
 *
 * The four catalogues are the four backend route segments. Unlike the LifeScribe
 * original (whose "Add" button was hard-wired to the genre endpoint regardless of
 * the open tab) every action on this page is derived from the active TabConfig,
 * so there is exactly one place that decides which catalogue is being written to.
 */

import { CatalogueKey, KeyField, TabConfig, UsageCategory } from './types';

/** Maximum simultaneously-active styles per catalogue (the app grid is 2×2). */
export const MAX_ACTIVE_PER_CATALOGUE = 4;

/** The identity column each catalogue is addressed by. */
export const KEY_FIELD: Record<CatalogueKey, KeyField> = {
  'writing-genres': 'genre_key',
  'writing-authors': 'author_key',
  'illustration-styles': 'style_key',
  'illustration-artists': 'style_key',
};

export const TAB_CONFIGS: TabConfig[] = [
  {
    label: 'Writing Genres',
    catalogue: 'writing-genres',
    family: 'writing',
    pillLabel: 'GENRE',
    noun: 'writing genre',
    popularField: 'writing',
    popularValue: 'genre',
  },
  {
    label: 'Writing Authors',
    catalogue: 'writing-authors',
    family: 'writing',
    pillLabel: 'AUTHOR',
    noun: 'writing author',
    popularField: 'writing',
    popularValue: 'author',
  },
  {
    label: 'Illustration Styles',
    catalogue: 'illustration-styles',
    family: 'illustration',
    pillLabel: 'STYLE',
    noun: 'illustration style',
    popularField: 'illustration',
    popularValue: 'style',
  },
  {
    label: 'Illustration Artists',
    catalogue: 'illustration-artists',
    family: 'illustration',
    pillLabel: 'ARTIST',
    noun: 'illustration artist',
    popularField: 'illustration',
    popularValue: 'artist',
  },
];

export const ANALYTICS_TAB_INDEX = TAB_CONFIGS.length;

/** Card category pill colours — ported verbatim from the LifeScribe original. */
export const CATEGORY_PILL_COLORS: Record<string, { bg: string; color: string }> = {
  GENRE: { bg: '#1A5F7A', color: '#fff' },
  AUTHOR: { bg: '#D4AC0D', color: '#fff' },
  STYLE: { bg: '#1A5F7A', color: '#fff' },
  ARTIST: { bg: '#7D1935', color: '#fff' },
};

/**
 * Analytics chart palette — ported verbatim from the LifeScribe original's
 * `components/ai/styles/constants.ts`. Both the "Styles by Category" pie and the
 * per-catalogue bars index into this by catalogue position, so a catalogue wears
 * the same colour everywhere on the Analytics tab.
 */
export const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

/** The chart colour for a catalogue, by its position in TAB_CONFIGS. */
export function catalogueColor(catalogue: CatalogueKey): string {
  const index = TAB_CONFIGS.findIndex((tab) => tab.catalogue === catalogue);
  return CHART_COLORS[(index < 0 ? 0 : index) % CHART_COLORS.length];
}

/**
 * Shown on both illustration tabs — a screen may only claim what the code does.
 *
 * This banner used to read "Stored in Quill's catalogue — not yet wired to the
 * app.", which was true until build 23 and false the moment it shipped. The
 * consumer now exists end to end: `quill_members.illustration_style_key`
 * (migration 569) holds the member's pick, quill-engine's
 * `/api/quill/illustration-styles` routes read and write it, and
 * `ChapterCreationAgent` puts the row's `ls_style_key` — LifeScribe's key, never
 * Quill's — on the compose payload's `illustration_style` field.
 *
 * Three facts an admin needs and cannot get anywhere else on this screen:
 *  • it reaches the app, and by which key;
 *  • it reaches NEW chapter pictures only — there is no re-render and no
 *    backfill path for chapters that already exist;
 *  • NULL is not a default. A member who never chose sends
 *    `illustration_style: null`, byte-identical to pre-build-23, and a member
 *    whose pick stopped being offerable sends null too (the engine's
 *    `resolveForCompose` refuses to substitute somebody else's look) — which is
 *    what deactivating a style here actually does to the members who chose it.
 */
export const ILLUSTRATION_BANNER =
  "Wired to the app: a member's pick is stored on their member row and travels to LifeScribe "
  + "as this row's LifeScribe style key. It styles new chapter pictures only — nothing "
  + 're-renders existing chapters. A member who has never chosen, or whose pick is no longer '
  + 'offered, composes with no illustration style rather than a substitute.';

/**
 * The same fact in one line, for the editor dialog's subtitle.
 *
 * A sibling constant rather than a second string: the dialog used to carry its
 * own hand-typed copy of the banner, which is exactly how two claims about the
 * same code path drift apart.
 */
export const ILLUSTRATION_DIALOG_SUBTITLE =
  "The member's pick reaches LifeScribe as this row's LifeScribe style key — new chapter "
  + 'pictures only';

/**
 * The four `quill_style_usage.category` values (migration 570), and the
 * catalogue each belongs to.
 *
 * The two writing values are NOT interchangeable: LifeScribe calls a GENRE a
 * "writing style" and an AUTHOR an "author", both arrive on its one
 * `writing_style` compose field, and 570's CHECK keeps them apart precisely so
 * this panel does not have to merge them into a number nobody asked for.
 */
export const USAGE_CATEGORY_BY_CATALOGUE: Record<CatalogueKey, UsageCategory> = {
  'writing-genres': 'writing_genre',
  'writing-authors': 'writing_author',
  'illustration-styles': 'illustration_style',
  'illustration-artists': 'illustration_artist',
};

export const CATALOGUE_BY_USAGE_CATEGORY: Record<UsageCategory, CatalogueKey> = {
  writing_genre: 'writing-genres',
  writing_author: 'writing-authors',
  illustration_style: 'illustration-styles',
  illustration_artist: 'illustration-artists',
};

/** Display order for usage sections — the catalogue tab order. */
export const USAGE_CATEGORIES: UsageCategory[] = TAB_CONFIGS.map(
  (tab) => USAGE_CATEGORY_BY_CATALOGUE[tab.catalogue]
);

/** The label a usage category wears on this screen — the catalogue's own tab label. */
export function usageCategoryLabel(category: UsageCategory): string {
  const catalogue = CATALOGUE_BY_USAGE_CATEGORY[category];
  return TAB_CONFIGS.find((tab) => tab.catalogue === catalogue)?.label ?? category;
}

/** Matches the style-usage route's own DEFAULT_WINDOW_DAYS. */
export const DEFAULT_USAGE_WINDOW_DAYS = 30;

/** Standing production caption on every Quill write confirmation. */
export const PRODUCTION_CAPTION =
  'This writes to the production Quill DB and affects real members.';

export const ACCENT_WRITING = '#1A5F7A';
export const ACCENT_ILLUSTRATION = '#146152';
